/**
 * TheNewsPulse — Community Contributor landing page.
 * Route: /contribute
 *
 * Marketing / invitation page. Signed-in users see the "Report a Story"
 * button routing to /contribute/new. Anonymous users see "Sign in to
 * contribute" (Firebase SocialPulse SSO).
 */
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  MapPin, Users, Camera, Feather, ShieldCheck, Heart,
  Megaphone, ClipboardList, Sparkles,
} from "lucide-react";

const TOPICS = [
  "Local and neighborhood news",
  "Community events and announcements",
  "Schools, colleges and education news",
  "Local government and civic updates",
  "Traffic, road closures and transport",
  "Weather, flooding and storm impact",
  "Emergencies and public safety",
  "Local business news",
  "Health and community wellness",
  "Sports and recreation",
  "Arts, music and cultural events",
  "Food, restaurants and local markets",
  "Environment and nature",
  "Volunteer activities and charities",
  "Missing-person community notices",
  "Obituaries and memorial announcements",
  "Photos and eyewitness reports",
  "Positive stories about local heroes",
];

const PILLARS = [
  {
    icon: MapPin,
    title: "Local voice, global reach",
    body: "From your neighborhood to the wider world — one useful story can help hundreds of people.",
  },
  {
    icon: Camera,
    title: "Eyewitness matters",
    body: "Your photo of a damaged road, a community fundraiser, or a school event may be the record your town needs.",
  },
  {
    icon: ShieldCheck,
    title: "Share responsibly",
    body: "Distinguish firsthand observations from second-hand information. Cite sources. Respect privacy and dignity.",
  },
  {
    icon: Heart,
    title: "Build community trust",
    body: "Our goal isn't more information — it's more useful, responsible and meaningful information.",
  },
];

function CTAButtons({ user }) {
  if (user) {
    return (
      <div className="flex flex-wrap gap-3">
        <Link
          to="/contribute/new"
          className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-full shadow-sm transition-colors"
          data-testid="contributor-cta-start"
        >
          <Feather className="w-4 h-4" /> Report a Story
        </Link>
        <Link
          to="/contribute/mine"
          className="inline-flex items-center gap-2 border border-slate-300 hover:border-slate-500 hover:bg-slate-50 text-slate-800 font-semibold px-6 py-3 rounded-full transition-colors"
          data-testid="contributor-cta-mine"
        >
          <ClipboardList className="w-4 h-4" /> My Contributions
        </Link>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap gap-3">
      <Link
        to={`/auth?next=${encodeURIComponent("/contribute/new")}`}
        className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-full shadow-sm transition-colors"
        data-testid="contributor-cta-signin"
      >
        <Users className="w-4 h-4" /> Sign in with SocialPulse SSO
      </Link>
      <span className="text-sm text-slate-500 self-center">
        Free account · takes ~30 seconds
      </span>
    </div>
  );
}

export default function NewsPulseContributor() {
  const { user } = useAuth() || {};

  return (
    <div className="max-w-[1100px] mx-auto px-4 lg:px-6 py-12 lg:py-16" data-testid="newspulse-contributor-page">
      {/* Hero */}
      <div className="text-center mb-14">
        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest font-bold text-blue-700 bg-blue-50 border border-blue-100 rounded-full px-4 py-1.5 mb-6">
          <Megaphone className="w-3.5 h-3.5" />
          Become a Community Reporter
        </div>
        <h1
          className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-slate-900 mb-6"
          style={{ fontFamily: "'Lora', 'Georgia', serif" }}
          data-testid="contributor-hero-title"
        >
          Your Community.<br />
          Your Stories.<br />
          <span className="text-blue-600">Your News.</span>
        </h1>
        <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto mb-8">
          Every community has stories worth telling — a road closes, a student
          wins an award, a local business opens, someone organises a food
          drive. Many of these never reach a newsroom, but they matter deeply
          to the people who live there.
        </p>
        <div className="flex justify-center">
          <CTAButtons user={user} />
        </div>
      </div>

      {/* Pillars */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
        {PILLARS.map((p) => (
          <div
            key={p.title}
            className="rounded-2xl border border-slate-200 bg-white p-5 hover:border-blue-200 hover:shadow-sm transition-all"
            data-testid={`contributor-pillar-${p.title.split(" ")[0].toLowerCase()}`}
          >
            <div className="w-10 h-10 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center mb-3">
              <p.icon className="w-5 h-5" strokeWidth={2.2} />
            </div>
            <h3 className="text-base font-bold text-slate-900 mb-1.5">{p.title}</h3>
            <p className="text-sm text-slate-600 leading-relaxed">{p.body}</p>
          </div>
        ))}
      </div>

      {/* What can you cover */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-16 items-start">
        <div>
          <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-3">
            What you can contribute
          </div>
          <h2 className="text-lg md:text-lg font-bold text-slate-900 mb-4"
              style={{ fontFamily: "'Lora', 'Georgia', serif" }}>
            Choose the topics that matter to you.
          </h2>
          <p className="text-sm text-slate-600 mb-4">
            You don't need to cover everything — pick the neighbourhoods,
            schools, sports, businesses or issues you care about. Contribute
            occasionally or become a regular community correspondent.
          </p>
        </div>
        <ul
          className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-slate-700"
          data-testid="contributor-topics-list"
        >
          {TOPICS.map((t) => (
            <li key={t} className="flex items-start gap-2">
              <Sparkles className="w-3.5 h-3.5 mt-1 shrink-0 text-blue-500" />
              <span>{t}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Trust manifesto */}
      <div className="rounded-2xl bg-slate-900 text-white p-8 lg:p-10 mb-16" data-testid="contributor-manifesto">
        <div className="text-[11px] uppercase tracking-widest font-bold text-blue-300 mb-2">
          Share Responsibly. Build Trust.
        </div>
        <h2 className="text-lg md:text-lg font-bold mb-4" style={{ fontFamily: "'Lora', 'Georgia', serif" }}>
          Community news becomes valuable when people can trust it.
        </h2>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-slate-200">
          <li className="flex gap-2"><ShieldCheck className="w-4 h-4 mt-0.5 text-blue-300 shrink-0" /> Share information you reasonably believe to be accurate.</li>
          <li className="flex gap-2"><ShieldCheck className="w-4 h-4 mt-0.5 text-blue-300 shrink-0" /> Distinguish firsthand observations from second-hand information.</li>
          <li className="flex gap-2"><ShieldCheck className="w-4 h-4 mt-0.5 text-blue-300 shrink-0" /> Provide sources or supporting links whenever possible.</li>
          <li className="flex gap-2"><ShieldCheck className="w-4 h-4 mt-0.5 text-blue-300 shrink-0" /> Respect privacy, copyright, personal dignity, and the safety of others.</li>
        </ul>
      </div>

      {/* Final CTA */}
      <div className="text-center border-t border-slate-200 pt-12">
        <h3 className="text-lg md:text-lg font-bold text-slate-900 mb-3"
            style={{ fontFamily: "'Lora', 'Georgia', serif" }}>
          See something worth sharing? Tell your community.
        </h3>
        <p className="text-sm text-slate-600 max-w-xl mx-auto mb-6">
          <span className="italic">Report Local. Share Responsibly. Inform Your Community. Preserve Its Stories.</span>
        </p>
        <div className="flex justify-center">
          <CTAButtons user={user} />
        </div>
      </div>
    </div>
  );
}
